// WWI trench barracks: a long timber hut under a pitched dark corrugated-style
// roof with a terracotta ridge accent (the homes/civic code), a stovepipe, and
// bunks hinted in the open porch end. Sandbags and stores fill the yard. 2x2.
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, gable, slab, poles } = styleV3;

const KHAKI = "#7a6f4e";
const MUD = "#5d4a33";
const CANVAS = "#cbbd93";
const GUNMETAL = "#4a4d52";
const TIMBER = "#8b6438";
const TIMBER_DK = "#6a4c2a";
const TERRACOTTA = "#c4593a";  // the homes/civic roof accent
const SHADOW = "#332e26";

const BASE = 0.12;                       // foundation slab top
// The hut runs east-west across the back of the plot; the yard fronts +z.
const HZ = -0.18;
const WALL_H = 0.42;
const WALL_W = 1.4, WALL_X = -0.125;     // the enclosed body
const ROOF_L = 1.76, ROOF_X = -0.05;     // roof overhangs east to make a porch
const HFRONT = HZ + 0.46;                // the hut's +z face

function walls(h: number): Part {
  return box(WALL_W, h, 0.92, TIMBER, [WALL_X, BASE + h / 2, HZ]);
}

// The pitched dark roof, ridge along x — ONE part — with a terracotta ridge cap.
function roof(): Part[] {
  return [
    gable(1.08, ROOF_L, 0.34, GUNMETAL, ROOF_X, BASE + WALL_H, HZ, "x"),
    box(1.8, 0.05, 0.1, TERRACOTTA, [ROOF_X, BASE + WALL_H + 0.34, HZ]),
  ];
}

function stovepipe(): Part {
  return cyl(0.04, 0.04, 0.42, 6, "#3a3d41", [-0.55, 0.85, -0.05]);
}

// The dark interior shadow closing the hut's east end (the porch backdrop).
function endShadow(): Part {
  return box(0.03, 0.38, 0.8, SHADOW, [WALL_X + WALL_W / 2 + 0.015, BASE + 0.19, HZ]);
}

// Bunks hinted in the open porch end under the roof overhang.
function bunks(): Part[] {
  return [
    box(0.2, 0.05, 0.6, CANVAS, [0.69, BASE + 0.16, HZ]),
    box(0.2, 0.05, 0.6, KHAKI, [0.69, BASE + 0.34, HZ]),
    box(0.04, 0.44, 0.04, TIMBER_DK, [0.79, BASE + 0.22, HZ - 0.28]),
    box(0.04, 0.44, 0.04, TIMBER_DK, [0.79, BASE + 0.22, HZ + 0.28]),
  ];
}

// Khaki canvas window flaps on the front face.
function windowFlaps(): Part[] {
  return [
    box(0.16, 0.14, 0.03, KHAKI, [-0.45, BASE + 0.28, HFRONT + 0.015]),
    box(0.16, 0.14, 0.03, KHAKI, [-0.05, BASE + 0.28, HFRONT + 0.015]),
  ];
}

// The front yard: a sandbag row, a rolled canvas tarp and a crate.
function yard(): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < 4; i++) {
    parts.push(box(0.18, 0.09, 0.12, i % 2 ? KHAKI : MUD,
      [-0.55 + i * 0.2, BASE + 0.045, 0.42], [0, (i % 2) * 0.24 - 0.12, 0]));
  }
  parts.push(cyl(0.09, 0.09, 0.5, 7, CANVAS, [0.45, BASE + 0.09, 0.62], [0, 0, Math.PI / 2]));
  parts.push(box(0.2, 0.2, 0.2, TIMBER_DK, [-0.6, BASE + 0.1, 0.68]));
  return parts;
}

export const trenchBarracks = defineModel({
  id: "trench-barracks",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Trench Barracks",
  tags: ["building", "wwi", "military", "barracks", "home", "quarters"],
  summary: "A long timber barracks hut with a dark roof and terracotta ridge.",
  description:
    "Timber-and-corrugated barracks hut housing a squad: a pitched dark roof " +
    "with a terracotta ridge accent, khaki canvas window flaps, and bunks " +
    "hinted through the open porch end. Sandbags and stores line the yard.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [slab(1.94, 1.94)] },
    { id: "lower", label: "Lower", parts: () => [
      slab(1.94, 1.94), walls(0.18), ...poles(1.7, 1.7, 0.6),
    ] },
    { id: "upper", label: "Upper", parts: () => [
      slab(1.94, 1.94), walls(WALL_H), endShadow(), ...windowFlaps(), ...poles(1.7, 1.7, 1.0),
    ] },
    { id: "complete", label: "Complete", parts: () => [
      slab(1.94, 1.94), walls(WALL_H), ...roof(), stovepipe(), endShadow(),
      ...bunks(), ...windowFlaps(), ...yard(),
    ] },
  ],
  animations: [],
});
